import { useState, type DragEvent, type ReactNode } from "react";
import {
  DRUM_PAD_COUNT,
  type DrumKit,
  type DrumPad,
} from "../../../../src/lib/audio";
import {
  audioFilesFromDrop,
  isAudioFileDrag,
  isLibrarySampleDrag,
  isSamplerSampleDrag,
  sampleIdFromDrop,
} from "../lib/sampleDrop";

export type DrumPadGridProps = {
  kit: DrumKit;
  selectedPad: number;
  onSelectPad: (index: number) => void;
  onAuditionPad: (index: number) => void;
  /** Assign one or more library sample ids starting at pad index. */
  onAssignLibrarySamples: (startIndex: number, samples: { id: string; name: string }[]) => void;
  /** Import OS files then assign from pad index (caller loads files). */
  onAssignOsFiles: (startIndex: number, files: File[]) => void;
  className?: string;
};

function padLabel(pad: DrumPad, index: number): string {
  return pad.name?.trim() || `Pad ${index + 1}`;
}

/**
 * 8×8 drum pad matrix — select/audition + library / OS audio drop targets.
 */
export function DrumPadGrid({
  kit,
  selectedPad,
  onSelectPad,
  onAuditionPad,
  onAssignLibrarySamples,
  onAssignOsFiles,
  className = "",
}: DrumPadGridProps): ReactNode {
  const [dragOverPad, setDragOverPad] = useState<number | null>(null);
  const sampleMode = kit.mode === "sample";

  function acceptDrag(event: DragEvent, index: number): void {
    if (!isSamplerSampleDrag(event) && !isAudioFileDrag(event) && !isLibrarySampleDrag(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setDragOverPad(index);
  }

  return (
    <div
      className={`grid grid-cols-8 gap-[0.3rem] ${className}`}
      data-pad-grid=""
      onDragLeave={event => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
          return;
        }
        setDragOverPad(null);
      }}
    >
      {Array.from({ length: DRUM_PAD_COUNT }, (_, i) => {
        const p = kit.pads[i]!;
        const active = i === selectedPad;
        const hasSample = Boolean(p.sampleId);
        const filled = sampleMode ? hasSample : p.level > 0;
        const over = dragOverPad === i;
        const tone = over
          ? "border-lab-accent bg-[rgba(61,139,253,0.35)] text-white ring-1 ring-lab-accent/60"
          : active
            ? "border-lab-accent bg-[rgba(61,139,253,0.22)] text-white"
            : filled
              ? "border-lab-line bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
              : "border-lab-line bg-transparent text-lab-muted/70 hover:bg-white/[0.04]";
        return (
          <button
            key={i}
            type="button"
            data-pad-index={i}
            className={`min-h-[2.15rem] truncate rounded border px-0.5 py-1 text-[9px] leading-tight ${tone}`}
            title={
              sampleMode
                ? `Pad ${i + 1} · MIDI ${kit.baseNote + i}${hasSample ? ` · ${p.sampleId}` : " · empty"}`
                : `MIDI ${kit.baseNote + i}`
            }
            onClick={() => {
              onSelectPad(i);
              onAuditionPad(i);
            }}
            onDragOver={event => acceptDrag(event, i)}
            onDragEnter={event => acceptDrag(event, i)}
            onDrop={event => {
              event.preventDefault();
              event.stopPropagation();
              setDragOverPad(null);
              const libraryId = sampleIdFromDrop(event);
              if (libraryId) {
                const name = event.dataTransfer.getData("text/plain") || libraryId;
                onAssignLibrarySamples(i, [{ id: libraryId, name }]);
                return;
              }
              const files = audioFilesFromDrop(event);
              if (files.length > 0) {
                onAssignOsFiles(i, files);
              }
            }}
          >
            {padLabel(p, i)}
          </button>
        );
      })}
    </div>
  );
}
